import React, { memo, useCallback, useId, useState } from 'react'

import {
  ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Icon, classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, number, oneOfType, string,
} from 'prop-types'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import useBreakpoint from 'Utils/useBreakpoint'

import useRunReferences from './useRunReferences'

const toggleIcon = glyph => <Icon svgComponent={glyph} size={iconSize} notSpaced color="darker" />

// A column down the canvas card's top-left on desktop (Figma 11216:303046), a strip along
// its bottom on phones (11225:307203).
const TOGGLE_ICONS = {
  column: { collapsed: toggleIcon(ChevronDown), expanded: toggleIcon(ChevronUp) },
  strip: { collapsed: toggleIcon(ChevronRight), expanded: toggleIcon(ChevronLeft) },
}

// Past this many, each layout shows only that many behind the toggle.
const COLLAPSED_COUNT = { column: 3, strip: 1 }

const PHONE_BREAKPOINTS = ['xs']

// The images an image was generated from, over the full-width editor's canvas and the
// opened image modal's stage. Stays mounted while the editor's canvas switches versions
// (isShown toggles), so the run is fetched once. customClass lets a host adjust its placement,
// and stripBreakpoints names the breakpoints its strip layout applies at.
function References({
  runId = null, isShown = true, customClass = '', stripBreakpoints = PHONE_BREAKPOINTS,
}) {
  const t = useTranslation()
  const labelId = useId()
  const references = useRunReferences(runId)
  const layout = stripBreakpoints.includes(useBreakpoint()) ? 'strip' : 'column'
  const isStrip = layout === 'strip'
  const [isExpanded, setIsExpanded] = useState(false)

  const handleToggle = useCallback(() => setIsExpanded(previous => !previous), [])

  if (!isShown || references.length === 0) {
    return null
  }

  const icons = TOGGLE_ICONS[layout]
  const collapsedCount = COLLAPSED_COUNT[layout]
  const isCollapsible = references.length > collapsedCount
  const shown = isCollapsible && !isExpanded ? references.slice(0, collapsedCount) : references
  // The strip draws a divider between the uploaded images and the styles.
  const firstStyleIndex = references.findIndex(reference => reference.isStyle)
  const hasDivider = firstStyleIndex > 0
  const label = t('shared.references.label')

  return (
    <div
      className={classnames('image-references', `image-references--${layout}`, customClass)}
      role="group"
      aria-labelledby={isStrip ? undefined : labelId}
      aria-label={isStrip ? label : undefined}
    >
      {!isStrip && (
        <p id={labelId} className="image-references__label cgt-caption">{label}</p>
      )}
      <ul
        className={classnames(
          `image-references__list image-references__list--${layout}`,
          isStrip && isExpanded && 'image-references__list--under-toggle',
        )}
        // Expanded, the list can overflow and scroll with its scrollbar hidden, and the
        // thumbnails take no focus, so the list itself is the keyboard's way to scroll it.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={isExpanded ? 0 : undefined}
      >
        {shown.map((reference, index) => (
          <li
            key={reference.id}
            className={classnames(
              'image-references__item',
              isStrip && 'image-references__item--strip',
              isStrip && hasDivider && index === firstStyleIndex && 'image-references__item--divided',
            )}
          >
            <img
              className="image-references__image"
              src={reference.url}
              alt={t('shared.references.imageAlt')}
              loading="lazy"
            />
          </li>
        ))}
      </ul>
      {isCollapsible && (
        <button
          type="button"
          className={classnames(
            'image-references__toggle',
            isStrip && isExpanded && 'image-references__toggle--floating',
          )}
          aria-expanded={isExpanded}
          aria-label={t(`shared.references.${isExpanded ? 'showFewer' : 'showAll'}`)}
          onClick={handleToggle}
        >
          {icons[isExpanded ? 'expanded' : 'collapsed']}
        </button>
      )}
    </div>
  )
}

References.propTypes = {
  runId: oneOfType([string, number]),
  isShown: bool,
  customClass: string,
  stripBreakpoints: arrayOf(string),
}

export default memo(References)
